/**
 * PulseBar — Live activity counter bar displayed between Hero and Nav
 *
 * Shows real-time counters: active members, in-game players, live streams,
 * active competitions, and a countdown to the next scheduled event.
 * Only renders when at least one counter > 0 or a nextEvent exists.
 *
 * Responsive: full labels on desktop, icons + values only on mobile.
 */
import { useState, useEffect, useCallback } from "react";
import { FaUsers, FaGamepad, FaVideo, FaClock, FaTrophy } from "react-icons/fa";
import { HubPulseData } from "@/hooks/hub/useHubPulse";
import "./PulseBar.scss";

interface PulseBarProps {
    pulse: HubPulseData | undefined;
    isLoading: boolean;
}

function formatCountdown(targetMs: number, nowMs: number): string {
    const diff = Math.max(0, targetMs - nowMs);
    const totalMinutes = Math.floor(diff / 60000);
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    if (hours > 0) {
        return `${hours}h ${minutes}m`;
    }
    if (minutes > 0) {
        return `${minutes}m`;
    }
    return "Soon";
}

export const PulseBar = ({ pulse, isLoading }: PulseBarProps) => {
    const [now, setNow] = useState(Date.now());

    const hasNextEvent = pulse?.nextEvent && pulse.nextEvent.scheduledStart > now;
    const hasContent = pulse && (
        pulse.activeMembers > 0 ||
        pulse.inGame > 0 ||
        pulse.liveStreams > 0 ||
        pulse.activeCompetitions > 0 ||
        hasNextEvent
    );

    const updateNow = useCallback(() => {
        setNow(Date.now());
    }, []);

    useEffect(() => {
        if (!hasNextEvent) return;

        const interval = setInterval(updateNow, 60000);
        return () => clearInterval(interval);
    }, [hasNextEvent, updateNow]);

    if (isLoading || !hasContent) {
        return null;
    }

    return (
        <div className="pulse-bar">
            <div className="pulse-bar__counters">
                {pulse.activeMembers > 0 &&
                    <div className="pulse-bar__counter" aria-label={`${pulse.activeMembers} active members`}>
                        <FaUsers className="pulse-bar__icon" aria-hidden="true" />
                        <span className="pulse-bar__value">{pulse.activeMembers}</span>
                        <span className="pulse-bar__label">Active</span>
                    </div>
                }

                {pulse.inGame > 0 &&
                    <div className="pulse-bar__counter" aria-label={`${pulse.inGame} players in-game`}>
                        <FaGamepad className="pulse-bar__icon pulse-bar__icon--game" aria-hidden="true" />
                        <span className="pulse-bar__value">{pulse.inGame}</span>
                        <span className="pulse-bar__label">In-Game</span>
                    </div>
                }

                {pulse.liveStreams > 0 &&
                    <div className="pulse-bar__counter pulse-bar__counter--live" aria-label={`${pulse.liveStreams} live streams`}>
                        <FaVideo className="pulse-bar__icon pulse-bar__icon--live" aria-hidden="true" />
                        <span className="pulse-bar__value">{pulse.liveStreams}</span>
                        <span className="pulse-bar__label">Live</span>
                    </div>
                }

                {pulse.activeCompetitions > 0 &&
                    <div className="pulse-bar__counter" aria-label={`${pulse.activeCompetitions} active competitions`}>
                        <FaTrophy className="pulse-bar__icon pulse-bar__icon--competition" aria-hidden="true" />
                        <span className="pulse-bar__value">{pulse.activeCompetitions}</span>
                        <span className="pulse-bar__label">Competitions</span>
                    </div>
                }

                {hasNextEvent &&
                    <div
                        className="pulse-bar__counter pulse-bar__counter--next"
                        aria-label={`Next event: ${pulse.nextEvent!.name} in ${formatCountdown(pulse.nextEvent!.scheduledStart, now)}`}
                    >
                        <FaClock className="pulse-bar__icon" aria-hidden="true" />
                        <span className="pulse-bar__value">
                            {formatCountdown(pulse.nextEvent!.scheduledStart, now)}
                        </span>
                        <span className="pulse-bar__label pulse-bar__label--next">
                            {pulse.nextEvent!.name}
                        </span>
                    </div>
                }
            </div>
        </div>
    );
};
